import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Handshake, Copy, Trash } from "@phosphor-icons/react";

const TYPES = ["LSGD", "Retail", "Staff", "ServiceProvider"];

export default function PartnersList() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try { setRows((await api.get("/partners")).data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const copyLink = async (pid, kind = "register") => {
    try {
      const r = await api.get(`/partners/${pid}/link`);
      const raw = (kind === "profile" ? r.data.profile_url : r.data.register_url) || r.data.url;
      const url = raw.startsWith("/")
        ? `https://rescigo.in${raw}`
        : raw;
      await navigator.clipboard.writeText(url);
      toast.success(`${kind === "profile" ? "Profile" : "Signup"} link copied: ${url}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deactivate = async (pid) => {
    if (!window.confirm("Deactivate this partner? Existing customers stay tagged.")) return;
    try { await api.delete(`/partners/${pid}`); toast.success("Deactivated"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AppShell title="Partner Program" subtitle="LSGD / Retail / Staff / Service Provider partners with their own referral links"
      actions={
        <button onClick={() => setOpen(true)} data-testid="btn-new-partner"
                className="inline-flex items-center gap-2 h-10 px-4 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium">
          <Plus size={14} weight="bold"/>Add Partner
        </button>
      }>
      <div className="bg-white border border-[#E5E2D9] rounded-md overflow-hidden" data-testid="partners-table">
        <table className="w-full text-sm">
          <thead className="bg-[#F7F6F2] text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">
            <tr>
              <th className="text-left px-5 py-2.5">Name</th>
              <th className="text-left px-3 py-2.5">Type</th>
              <th className="text-left px-3 py-2.5">Phone</th>
              <th className="text-left px-3 py-2.5">Commission</th>
              <th className="text-left px-3 py-2.5">Status</th>
              <th className="text-right px-5 py-2.5">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="px-5 py-12 text-center text-[#8A8780]">
              <Handshake size={28} className="inline-block mb-3 opacity-50"/><br/>No partners yet.
            </td></tr>}
            {rows.map(p => (
              <tr key={p.id} className="border-t border-[#F0ECE3]" data-testid={`partner-${p.id}`}>
                <td className="px-5 py-3 font-medium">
                  <Link to={`/customers/referrals/partners/${p.id}`} className="hover:underline text-[#1A1A1A]" data-testid={`open-${p.id}`}>
                    {p.name}
                  </Link>
                </td>
                <td className="px-3 py-3">
                  <span className="px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] rounded bg-[#D4E0D1] text-[#2D5A27]">{p.type}</span>
                </td>
                <td className="px-3 py-3 font-mono text-xs">{p.phone}</td>
                <td className="px-3 py-3">
                  {p.commission_enabled === false ? (
                    <span className="px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] rounded bg-[#F7F6F2] text-[#8A8780] border border-[#E5E2D9]">OFF</span>
                  ) : (
                    <span className="px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] rounded bg-[#D4E0D1] text-[#2D5A27]">ON · 10%</span>
                  )}
                </td>
                <td className="px-3 py-3">
                  {p.active === false ? (
                    <span className="text-xs text-[#C25D38]">Inactive</span>
                  ) : (
                    <span className="text-xs text-[#2D5A27]">Active</span>
                  )}
                </td>
                <td className="px-5 py-3 text-right space-x-3">
                  <Link to={`/customers/referrals/partners/${p.id}`} data-testid={`profileview-${p.id}`}
                        className="inline-flex items-center gap-1 text-xs text-[#2D5A27] hover:underline font-medium">
                    Open
                  </Link>
                  <button onClick={() => copyLink(p.id, "register")} data-testid={`copy-${p.id}`}
                          className="inline-flex items-center gap-1 text-xs text-[#2D5A27] hover:underline">
                    <Copy size={12}/>Signup
                  </button>
                  <button onClick={() => copyLink(p.id, "profile")} data-testid={`profile-${p.id}`}
                          className="inline-flex items-center gap-1 text-xs text-[#2D5A27] hover:underline">
                    <Copy size={12}/>Public link
                  </button>
                  {p.active !== false && (
                    <button onClick={() => deactivate(p.id)} data-testid={`deact-${p.id}`}
                            className="inline-flex items-center gap-1 text-xs text-[#C25D38] hover:underline">
                      <Trash size={12}/>Deactivate
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && <PartnerForm onClose={() => setOpen(false)} onSaved={load}/>}
    </AppShell>
  );
}

const PartnerForm = ({ onClose, onSaved }) => {
  const [f, setF] = useState({
    name: "", phone: "", type: "Retail", notes: "",
    commission_enabled: true,
    commission_pct: 0.10,
    commission_base: "package_price",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (f.name.trim().length < 2) { toast.error("Name required"); return; }
    if (f.phone.trim().length < 8) { toast.error("Phone required"); return; }
    setBusy(true);
    try { await api.post("/partners", f); toast.success("Partner created"); onSaved?.(); onClose(); }
    catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-md max-w-md w-full p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="partner-form">
        <div className="font-heading text-lg mb-4">Add Partner</div>
        <L>Name *</L>
        <input value={f.name} onChange={e => setF(s => ({ ...s, name: e.target.value }))}
               data-testid="pf-name" className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm mb-3"/>
        <L>Phone *</L>
        <input value={f.phone} onChange={e => setF(s => ({ ...s, phone: e.target.value }))}
               data-testid="pf-phone" className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm mb-3 font-mono"/>
        <L>Partner Type *</L>
        <select value={f.type} onChange={e => setF(s => ({ ...s, type: e.target.value }))}
                data-testid="pf-type" className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white text-sm mb-3">
          {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>

        {/* Commission toggle */}
        <L>Commission Reward *</L>
        <div className={`flex items-center gap-3 p-3 border rounded-md mb-3 ${f.commission_enabled ? "border-[#2D5A27] bg-[#EFF3EE]" : "border-[#E5E2D9] bg-[#F7F6F2]"}`}>
          <button type="button" onClick={() => setF(s => ({ ...s, commission_enabled: !s.commission_enabled }))}
                  data-testid="pf-commission"
                  className={`relative w-12 h-6 rounded-full transition-colors ${f.commission_enabled ? "bg-[#2D5A27]" : "bg-[#C7C3B7]"}`}>
            <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${f.commission_enabled ? "left-6" : "left-0.5"}`}/>
          </button>
          <div className="flex-1">
            <div className="text-sm font-medium text-[#1A1A1A]">{f.commission_enabled ? "Commission ENABLED" : "Commission DISABLED"}</div>
            <div className="text-[11px] text-[#5C5A55]">
              {f.commission_enabled
                ? "Partner earns a % of each referred customer's first package payment."
                : "Partner gets the link only — no commission credited."}
            </div>
          </div>
        </div>

        {f.commission_enabled && (
          <>
            <L>Commission Percentage *</L>
            <div className="grid grid-cols-3 gap-2 mb-3" data-testid="pf-pct">
              {[0.02, 0.05, 0.10].map(p => (
                <button key={p} type="button" data-testid={`pf-pct-${Math.round(p*100)}`}
                        onClick={() => setF(s => ({ ...s, commission_pct: p }))}
                        className={`h-10 rounded-md text-sm font-medium transition border ${
                          f.commission_pct === p
                            ? "bg-[#2D5A27] text-white border-[#2D5A27]"
                            : "bg-white text-[#5C5A55] border-[#E5E2D9] hover:bg-[#F7F6F2]"
                        }`}>
                  {Math.round(p*100)}%{p === 0.10 ? " (default)" : ""}
                </button>
              ))}
            </div>

            <L>Commission applied on *</L>
            <div className="space-y-2 mb-3" data-testid="pf-base">
              {[
                { v: "package_price",  l: "Package price only", h: "10% of the package master price (recommended — default)" },
                { v: "invoice_total",  l: "Total invoice amount of package", h: "10% of the full invoice total (incl. extras + GST)" },
              ].map(opt => (
                <label key={opt.v} data-testid={`pf-base-${opt.v}`}
                       className={`flex items-start gap-2 p-2.5 border rounded-md cursor-pointer transition ${
                          f.commission_base === opt.v ? "border-[#2D5A27] bg-[#EFF3EE]" : "border-[#E5E2D9] hover:bg-[#F7F6F2]"
                       }`}>
                  <input type="radio" name="comm-base" value={opt.v}
                         checked={f.commission_base === opt.v}
                         onChange={() => setF(s => ({ ...s, commission_base: opt.v }))}
                         className="mt-0.5 accent-[#2D5A27]"/>
                  <div className="flex-1">
                    <div className="text-sm">{opt.l}</div>
                    <div className="text-[11px] text-[#5C5A55]">{opt.h}</div>
                  </div>
                </label>
              ))}
            </div>
          </>
        )}

        <L>Notes</L>
        <textarea value={f.notes} onChange={e => setF(s => ({ ...s, notes: e.target.value }))} rows={2}
                  data-testid="pf-notes" className="w-full px-3 py-2 border border-[#E5E2D9] rounded-md text-xs mb-4"/>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="h-10 px-4 border border-[#E5E2D9] rounded-md text-sm">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="pf-save"
                  className="h-10 px-4 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-60 text-white rounded-md text-sm font-medium">
            {busy ? "Saving…" : "Create Partner"}
          </button>
        </div>
      </div>
    </div>
  );
};

const L = ({ children }) => (
  <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-1.5">{children}</label>
);
